import { IconCategoryStyle } from "@components/atoms/Button/types";
import {
  PAUserSubTabsType,
  PriceModes,
} from "@components/molecules/PriceModeCard/types";
import { RequestTypes } from "@scenes/requests/utils/utils";
import { ProductStockType } from "@utils/constants";
import { VDBIcons } from "@utils/VDBIcons";
import { useTranslation } from "react-i18next";

const getStyleForCategory = (type: string): IconCategoryStyle => {
  return {
    textColor: `${type}-01`,
  };
};

export { getStyleForCategory };

export const getProductSpecificColor = (productType: string) => {
  switch (productType) {
    case ProductStockType.DIAMOND:
      return "diam-06";
    case ProductStockType.GEMSTONE:
      return "gem-06";
    case ProductStockType.LAB_GROWN_DIAMOND:
      return "lab-06";
    case ProductStockType.JEWELRY:
      return "jewelry-06";
    case ProductStockType.DEFAULT:
    default:
      return "core-04";
  }
};

interface RequestTypeMap {
  name: string;
  theme: string;
}

const requestTypeMap = (
  t: (key: string) => string
): Record<string, RequestTypeMap> => ({
  Diamond: {
    name: t("global.text.naturalDiamonds", "Natural Diamonds"),
    theme: "diam-06",
  },
  Gemstone: {
    name: t("global.text.coloredGemstones", "Colored Gemstones"),
    theme: "gem-06",
  },
  Jewelry: { name: t("global.text.jewelry", "Jewelry"), theme: "jewelry-06" },
  LabGrownDiamond: {
    name: t("global.text.labGrownDiamonds", "Lab-Grown Diamonds"),
    theme: "lab-06",
  },
  LabGrownJewelry: {
    name: t("global.text.labGrownJewelry", "Lab-Grown Jewelry"),
    theme: "jewelry-06",
  },
  MultipleOrder: {
    name: t("global.text.multipleOrder", "Multiple Order"),
    theme: "Main-06",
  },
});

interface RequestThemeMap {
  border_theme: string;
}

const requestThemeMap: Record<string, RequestThemeMap> = {
  pending: { border_theme: "warn-01" },
  "partially pending": { border_theme: "warn-01" },
  "partially accepted": { border_theme: "success-01" },
  accepted: { border_theme: "success-01" },
  withdraw: { border_theme: "error-01" },
  received: { border_theme: "success-01" },
  expired: { border_theme: "error-01" },
  decline: { border_theme: "error-01" },
  completed: { border_theme: "success-01" },
  submitted: { border_theme: "success-01" },
};

interface RequestIconMap {
  request_name: string;
  request_icon: string;
}

export const getRequestIconMap = (
  t: (key: string) => string
): Record<string, RequestIconMap> => ({
  buy: {
    request_name: t("requestHistory.receivedTab.buy.text", "Buy"),
    request_icon: VDBIcons.BUY,
  },
  memo: {
    request_name: t("requestHistory.receivedTab.memo.text", "Memo"),
    request_icon: VDBIcons.MEMO,
  },
  hold: {
    request_name: t("requestHistory.receivedTab.hold.text", "Hold"),
    request_icon: VDBIcons.HOLD,
  },
  ask_question: {
    request_name: t("requestHistory.receivedTab.question.text", "Question"),
    request_icon: VDBIcons.ASK_REQUEST,
  },
  buy_diamond: {
    request_name: t("requestHistory.receivedTab.buy.text", "Buy"),
    request_icon: VDBIcons.BUY,
  },
  buy_jewelry: {
    request_name: t("requestHistory.receivedTab.buy.text", "Buy"),
    request_icon: VDBIcons.BUY,
  },
  buy_gemstone: {
    request_name: t("requestHistory.receivedTab.buy.text", "Buy"),
    request_icon: VDBIcons.BUY,
  },
  buy_lab_grown_diamond: {
    request_name: t("requestHistory.receivedTab.buy.text", "Buy"),
    request_icon: VDBIcons.BUY,
  },
  diamond_memo: {
    request_name: t("requestHistory.receivedTab.memo.text", "Memo"),
    request_icon: VDBIcons.MEMO,
  },
  lab_grown_diamond_memo: {
    request_name: t("requestHistory.receivedTab.memo.text", "Memo"),
    request_icon: VDBIcons.MEMO,
  },
  jewelry_memo: {
    request_name: t("requestHistory.receivedTab.memo.text", "Memo"),
    request_icon: VDBIcons.MEMO,
  },
  gemstone_memo: {
    request_name: t("requestHistory.receivedTab.memo.text", "Memo"),
    request_icon: VDBIcons.MEMO,
  },
  diamond_ask: {
    request_name: t("requestHistory.receivedTab.question.text", "Question"),
    request_icon: VDBIcons.ASK_REQUEST,
  },
  jewelry_ask: {
    request_name: t("requestHistory.receivedTab.question.text", "Question"),
    request_icon: VDBIcons.ASK_REQUEST,
  },
  lab_grown_diamond_ask: {
    request_name: t("requestHistory.receivedTab.question.text", "Question"),
    request_icon: VDBIcons.ASK_REQUEST,
  },
  gemstone_ask: {
    request_name: t("requestHistory.receivedTab.question.text", "Question"),
    request_icon: VDBIcons.ASK_REQUEST,
  },
  hold_diamond: {
    request_name: t("requestHistory.receivedTab.hold.text", "Hold"),
    request_icon: VDBIcons.HOLD,
  },
  hold_gemstone: {
    request_name: t("requestHistory.receivedTab.hold.text", "Hold"),
    request_icon: VDBIcons.HOLD,
  },
  hold_jewelry: {
    request_name: t("requestHistory.receivedTab.hold.text", "Hold"),
    request_icon: VDBIcons.HOLD,
  },
  bulk_quote_jewelry: {
    request_name: t("requestHistory.receivedTab.quote.text", "Quote"),
    request_icon: VDBIcons.QUOTE,
  },
  hold_lab_grown_diamond: {
    request_name: t("requestHistory.receivedTab.hold.text", "Hold"),
    request_icon: VDBIcons.HOLD,
  },
  bulk_buy_diamond: {
    request_name: t("requestHistory.receivedTab.buy.text", "Buy"),
    request_icon: VDBIcons.BUY,
  },
  bulk_quote_diamond: {
    request_name: t("requestHistory.receivedTab.quote.text", "Quote"),
    request_icon: VDBIcons.QUOTE,
  },
  bulk_diamond_buy: {
    request_name: t("requestHistory.receivedTab.buy.text", "Buy"),
    request_icon: VDBIcons.BUY,
  },
  bulk_diamond_memo: {
    request_name: t("requestHistory.receivedTab.memo.text", "Memo"),
    request_icon: VDBIcons.MEMO,
  },
  bulk_diamond_hold: {
    request_name: t("requestHistory.receivedTab.hold.text", "Hold"),
    request_icon: VDBIcons.HOLD,
  },
  bulk_hold_diamond: {
    request_name: t("requestHistory.receivedTab.hold.text", "Hold"),
    request_icon: VDBIcons.HOLD,
  },
  bulk_memo_diamond: {
    request_name: t("requestHistory.receivedTab.memo.text", "Memo"),
    request_icon: VDBIcons.MEMO,
  },
  bulk_quote_gemstone: {
    request_name: t("requestHistory.receivedTab.quote.text", "Quote"),
    request_icon: VDBIcons.QUOTE,
  },
  bulk_hold_gemstone: {
    request_name: t("requestHistory.receivedTab.hold.text", "Hold"),
    request_icon: VDBIcons.HOLD,
  },
  bulk_gemstone_memo: {
    request_name: t("requestHistory.receivedTab.memo.text", "Memo"),
    request_icon: VDBIcons.MEMO,
  },
  bulk_buy_gemstone: {
    request_name: t("requestHistory.receivedTab.buy.text", "Buy"),
    request_icon: VDBIcons.BUY,
  },
  bulk_buy_lab_grown_diamond: {
    request_name: t("requestHistory.receivedTab.buy.text", "Buy"),
    request_icon: VDBIcons.BUY,
  },
  bulk_lab_grown_diamond_memo: {
    request_name: t("requestHistory.receivedTab.memo.text", "Memo"),
    request_icon: VDBIcons.MEMO,
  },
  bulk_hold_lab_grown_diamond: {
    request_name: t("requestHistory.receivedTab.hold.text", "Hold"),
    request_icon: VDBIcons.HOLD,
  },
  bulk_quote_lab_grown_diamond: {
    request_name: t("requestHistory.receivedTab.quote.text", "Quote"),
    request_icon: VDBIcons.QUOTE,
  },
  bulk_buy_jewelry: {
    request_name: t("requestHistory.receivedTab.buy.text", "Buy"),
    request_icon: VDBIcons.BUY,
  },
  bulk_jewelry_memo: {
    request_name: t("requestHistory.receivedTab.memo.text", "Memo"),
    request_icon: VDBIcons.MEMO,
  },
  bulk_hold_jewelry: {
    request_name: t("requestHistory.receivedTab.hold.text", "Hold"),
    request_icon: VDBIcons.HOLD,
  },
  order: {
    request_name: t("requestHistory.receivedTab.orders.text", "Orders"),
    request_icon: VDBIcons.BULK_LIST_SHOPPING_CART,
  },
  sold: {
    request_name: t("requestHistory.receivedTab.sold.text", "Sold"),
    request_icon: VDBIcons.BUY,
  },
  return: {
    request_name: t("requestHistory.receivedTab.return.text", "Return"),
    request_icon: VDBIcons.REFRESH_CIRCLE,
  },
});

export function convertProductType(productType: any, t: any) {
  const formatDate = (dateString: string, isDate: boolean) => {
    const moment = require("moment");

    const date = moment(dateString);
    const today = moment();
    const yesterday = moment().subtract(1, "days");

    const getTime = (time: any) => {
      const formattedTime = time.format("hh:mm A");
      return formattedTime;
    };

    const getDate = (dates: any) => {
      const formattedDate = dates.format("D MMM, YYYY");
      return formattedDate;
    };

    if (date.isSame(today, "day")) {
      if (isDate) {
        return "Today";
      } else {
        return getTime(date);
      }
    } else if (date.isSame(yesterday, "day")) {
      if (isDate) {
        return "Yesterday";
      } else {
        return getTime(date);
      }
    } else {
      if (isDate) {
        return getDate(date);
      } else {
        return getTime(date);
      }
    }
  };

  if (Array.isArray(productType)) {
    return productType.map((item) => ({
      ...item,
      type_text:
        item?.children_count &&
        item?.children_count > 1 &&
        item.request_type === RequestTypes.Order
          ? requestTypeMap(t)?.MultipleOrder?.name
          : requestTypeMap(t)[item?.stock_item_type]?.name,
      theme: requestTypeMap(t)[item?.stock_item_type]?.theme,
      border_theme: requestThemeMap[item?.status]?.border_theme,
      request_icon: getRequestIconMap(t)[item?.request_type]?.request_icon,
      request_name: getRequestIconMap(t)[item.request_type]?.request_name,
      updated_at_date: formatDate(item.updated_at, true),
      updated_at_time: formatDate(item.updated_at, false),
    }));
  } else {
    // If productType is not an array, handle single item
    return {
      ...productType,
      type_text: requestTypeMap(t)[productType.stock_item_type].name,
      theme: requestTypeMap(t)[productType.stock_item_type].theme,
      border_theme: requestThemeMap[productType?.status]?.border_theme,
      request_icon:
        getRequestIconMap(t)[productType?.request_type]?.request_icon,
      request_name:
        getRequestIconMap(t)[productType?.request_type]?.request_name,
      updated_at_date: formatDate(productType?.updated_at, true),
      updated_at_time: formatDate(productType?.updated_at, false),
    };
  }
}

export const useInitialProductArray = () => {
  const { t } = useTranslation();

  return [
    {
      id: 1,
      title: t("requestHistory.receivedTab.all.text", "All"),
      description: "Description for item 1",
      icon: VDBIcons.GRID,
      producttypes: "all",
      selection: true,
    },
    {
      id: 2,
      title: t("requestHistory.receivedTab.buy.text", "Buy"),
      description: "Description for item 2",
      icon: VDBIcons.BUY,
      producttypes: "buy",
      selection: false,
    },
    {
      id: 3,
      title: t("requestHistory.receivedTab.memo.text", "Memo"),
      description: "Description for item 3",
      icon: VDBIcons.MEMO,
      producttypes: "memo",
      selection: false,
    },
    {
      id: 4,
      title: t("requestHistory.receivedTab.hold.text", "Hold"),
      description: "Description for item 3",
      icon: VDBIcons.HOLD,
      producttypes: "hold",
      selection: false,
    },
    {
      id: 5,
      title: t("requestHistory.receivedTab.question.text", "Question"),
      description: "Description for item 3",
      icon: VDBIcons.ASK_REQUEST,
      producttypes: "ask_question",
      selection: false,
    },
    {
      id: 6,
      title: t("requestHistory.receivedTab.iframe.text", "Iframe"),
      description: "Description for item 3",
      icon: VDBIcons.BUY,
      producttypes: "iframe",
      selection: false,
    },
    {
      id: 9,
      title: t("requestHistory.receivedTab.internal.text", "Internal"),
      description: "Description for item 3",
      icon: VDBIcons.BUY,
      producttypes: "internal_request",
      selection: false,
    },
    {
      id: 10,
      title: t("requestHistory.receivedTab.quote.text", "Quote"),
      description: "Description for item 3",
      icon: VDBIcons.QUOTE,
      producttypes: "bulk_quote",
      selection: false,
    },
    {
      id: 11,
      title: t("requestHistory.receivedTab.unpaidOrder.text"),
      description: "Description for item 3",
      icon: VDBIcons.BULK_LIST_SHOPPING_CART,
      producttypes: "non_payment_order",
      selection: false,
    },
    {
      id: 12,
      title: t("requestHistory.receivedTab.sold.text"),
      description: "Description for item 3",
      icon: VDBIcons.BUY,
      producttypes: "sold",
      selection: false,
    },
    {
      id: 13,
      title: t("requestHistory.receivedTab.return.text"),
      description: "Description for item 3",
      icon: VDBIcons.REFRESH_CIRCLE,
      producttypes: "return",
      selection: false,
    },
  ];
};

export function filteredProductArray(
  isPaFeature: boolean,
  isPurchasingAgent: boolean,
  isSalesPerson: boolean,
  isSupplierPerson: boolean,
  priceMode: PriceModes,
  enableAskQuestion?: boolean,
  isReceived?: boolean,
  showMemoInOrangeMode?: boolean,
  isPAUserOrPASalesPerson?: boolean,
  priceModeLockedInOrangeMode?: boolean,
  isCustomApp?: boolean,
  allowed_request_types?: string[]
) {
  const salesPersonTypes = [
    PAUserSubTabsType.All,
    PAUserSubTabsType.Buy,
    PAUserSubTabsType.Memo,
    PAUserSubTabsType.Hold,
  ];
  const ask_question = [PAUserSubTabsType.AskQuestion];
  const baseTypes = enableAskQuestion ? [...ask_question] : [];

  if (isCustomApp) {
    const purchasingAgentTypes: string[] = allowed_request_types ?? [];
    const productTypes = [...purchasingAgentTypes];
    const initialProductArray = useInitialProductArray();
    return initialProductArray.filter((product) =>
      productTypes.includes(product.producttypes)
    );
  }

  if (!isPAUserOrPASalesPerson && showMemoInOrangeMode) {
    let subTypes = [];
    if (isPaFeature) {
      if (!isReceived && priceMode === PriceModes.WHITE) {
        subTypes = [
          PAUserSubTabsType.All,
          PAUserSubTabsType.Buy,
          PAUserSubTabsType.Memo,
          PAUserSubTabsType.Hold,
          // PAUserSubTabsType.InternalRequest,
          PAUserSubTabsType.AskQuestion,
        ];
      } else {
        subTypes = [
          PAUserSubTabsType.All,
          PAUserSubTabsType.Buy,
          PAUserSubTabsType.Memo,
          PAUserSubTabsType.Hold,
          PAUserSubTabsType.InternalRequest,
          PAUserSubTabsType.AskQuestion,
        ];
      }
    } else {
      subTypes = [
        PAUserSubTabsType.All,
        PAUserSubTabsType.Buy,
        PAUserSubTabsType.Memo,
        PAUserSubTabsType.Hold,
        PAUserSubTabsType.AskQuestion,
      ];
    }
    //
    if (priceMode === PriceModes.WHITE && isReceived) {
      subTypes = [
        PAUserSubTabsType.All,
        PAUserSubTabsType.Buy,
        PAUserSubTabsType.Memo,
        PAUserSubTabsType.Hold,
        PAUserSubTabsType.InternalRequest,
        PAUserSubTabsType.AskQuestion,
      ];
    }
    //
    const productTypes = [...subTypes];
    const initialProductArray = useInitialProductArray();
    return initialProductArray.filter((product) =>
      productTypes.includes(product.producttypes)
    );
  }

  if (!isPurchasingAgent && !isPaFeature) {
    if (!showMemoInOrangeMode && priceModeLockedInOrangeMode) {
      let types = [];
      //
      const productTypes = [...types];
      //
      const initialProductArray = useInitialProductArray();
      return initialProductArray.filter((product) =>
        productTypes.includes(product.producttypes)
      );
    } else {
      let types = [
        PAUserSubTabsType.All,
        PAUserSubTabsType.Buy,
        PAUserSubTabsType.Memo,
        PAUserSubTabsType.Hold,
        PAUserSubTabsType.AskQuestion,
      ];
      //
      const productTypes = [...types];
      //
      const initialProductArray = useInitialProductArray();
      return initialProductArray.filter((product) =>
        productTypes.includes(product.producttypes)
      );
    }
  }
  if (isPAUserOrPASalesPerson && !isPurchasingAgent) {
    let types = [
      PAUserSubTabsType.All,
      PAUserSubTabsType.Buy,
      PAUserSubTabsType.Memo,
      PAUserSubTabsType.Hold,
      PAUserSubTabsType.InternalRequest,
      PAUserSubTabsType.AskQuestion,
    ];
    if (
      isPaFeature &&
      !isReceived &&
      !isPurchasingAgent &&
      priceMode === PriceModes.ORANGE
    ) {
      types = [PAUserSubTabsType.InternalRequest];
    }
    if (isReceived) {
      types = [
        PAUserSubTabsType.All,
        PAUserSubTabsType.Buy,
        PAUserSubTabsType.Memo,
        PAUserSubTabsType.Hold,
        PAUserSubTabsType.AskQuestion,
      ];
    }
    //
    const productTypes = [...types];
    //
    const initialProductArray = useInitialProductArray();
    return initialProductArray.filter((product) =>
      productTypes.includes(product.producttypes)
    );
  }

  if (!isPurchasingAgent && isPaFeature) {
    if (priceMode === PriceModes.WHITE) {
      let types = [
        PAUserSubTabsType.All,
        PAUserSubTabsType.Buy,
        PAUserSubTabsType.Memo,
        PAUserSubTabsType.Hold,
        PAUserSubTabsType.InternalRequest,
        PAUserSubTabsType.AskQuestion,
      ];
      if (!isReceived) {
        types = [
          PAUserSubTabsType.All,
          PAUserSubTabsType.Buy,
          PAUserSubTabsType.Memo,
          PAUserSubTabsType.Hold,
          PAUserSubTabsType.AskQuestion,
        ];
      }
      //
      const productTypes = [...types];
      //
      const initialProductArray = useInitialProductArray();
      return initialProductArray.filter((product) =>
        productTypes.includes(product.producttypes)
      );
    } else {
      let types = [PAUserSubTabsType.InternalRequest];
      const productTypes = [...types];
      //
      const initialProductArray = useInitialProductArray();
      return initialProductArray.filter((product) =>
        productTypes.includes(product.producttypes)
      );
    }
  }
  if (isPurchasingAgent) {
    let purchasingAgentTypes: string[] = [];
    if (isReceived) {
      if (priceMode === PriceModes.WHITE) {
        purchasingAgentTypes = [
          PAUserSubTabsType.All,
          PAUserSubTabsType.Buy,
          PAUserSubTabsType.Memo,
          PAUserSubTabsType.Hold,
          PAUserSubTabsType.InternalRequest,
          PAUserSubTabsType.AskQuestion,
        ];
      } else {
        purchasingAgentTypes = [PAUserSubTabsType.InternalRequest];
      }
    } else {
      purchasingAgentTypes = [
        PAUserSubTabsType.All,
        PAUserSubTabsType.Buy,
        PAUserSubTabsType.Memo,
        PAUserSubTabsType.Hold,
        PAUserSubTabsType.AskQuestion,
      ];

      if (priceMode === PriceModes.ORANGE) {
        purchasingAgentTypes = [
          PAUserSubTabsType.All,
          PAUserSubTabsType.Buy,
          PAUserSubTabsType.Memo,
          PAUserSubTabsType.Hold,
          PAUserSubTabsType.AskQuestion,
        ];
      }
    }
    const productTypes = [...purchasingAgentTypes];
    const initialProductArray = useInitialProductArray();
    return initialProductArray.filter((product) =>
      productTypes.includes(product.producttypes)
    );
  } else if (isSalesPerson) {
    if (priceMode === PriceModes.ORANGE) {
      let subTypes = [];
      const productTypes = [...subTypes, ...baseTypes];
      const initialProductArray = useInitialProductArray();
      return initialProductArray.filter((product) =>
        productTypes.includes(product.producttypes)
      );
    } else {
      const productTypes = [...salesPersonTypes, ...baseTypes];
      const initialProductArray = useInitialProductArray();
      return initialProductArray.filter((product) =>
        productTypes.includes(product.producttypes)
      );
    }
  } else if (isSupplierPerson) {
    if (priceMode === PriceModes.WHITE) {
      let subTypes = [
        PAUserSubTabsType.All,
        PAUserSubTabsType.Buy,
        PAUserSubTabsType.Memo,
        PAUserSubTabsType.Hold,
        PAUserSubTabsType.AskQuestion,
      ];
      const productTypes = [...subTypes, ...baseTypes];
      const initialProductArray = useInitialProductArray();
      return initialProductArray.filter((product) =>
        productTypes.includes(product.producttypes)
      );
    } else if (priceMode === PriceModes.ORANGE) {
      let subTypes = [];
      const productTypes = [...subTypes, ...baseTypes];
      const initialProductArray = useInitialProductArray();
      return initialProductArray.filter((product) =>
        productTypes.includes(product.producttypes)
      );
    }
  } else {
    const productTypes = baseTypes;
    const initialProductArray = useInitialProductArray();
    return initialProductArray.filter((product) =>
      productTypes.includes(product.producttypes)
    );
  }
}
